import { Image, Skeleton } from "antd";
import { useEffect, useRef, useState } from "react";
import { serverImgFullPath } from "../../../utils/Common";

export default function OrderCategorySlider({
                                              categories = [],
                                              selectedCategory,
                                              setSelectedCategory,
                                            }) {
  const scrollContainerRef = useRef(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);

  useEffect(() => {
    function updateScrollVisibility() {
      const el = scrollContainerRef.current;
      if (!el) return;
      setShowScrollButtons(el.scrollWidth > el.clientWidth);
    }

    updateScrollVisibility();

    window.addEventListener("resize", updateScrollVisibility);
    return () => window.removeEventListener("resize", updateScrollVisibility);
  }, [categories]);

  function onHandleScrollLeft() {
    scrollContainerRef.current?.scrollBy({
      left: -200,
      behavior: "smooth",
    });
  }

  function onHandleScrollRight() {
    scrollContainerRef.current?.scrollBy({
      left: 200,
      behavior: "smooth",
    });
  }

  function validateImagePath(url) {
    if (!url) return "/images/icons/no-pic-icon.svg";
    const path = url.includes("\\")
      ? url.replaceAll("\\", "/")
      : url;
    return process.env.REACT_APP_IMAGE_BASE_URL + path;
  }

  return (
    <div className="w-full flex items-center gap-x-[5px] overflow-hidden">
      {showScrollButtons && (
        <button
          className="w-[29px] h-[29px] bg-[#AFD467] rounded-full flex items-center justify-center shrink-0"
          onClick={onHandleScrollLeft}
        >
          <Image
            src="/images/icons/arrow.svg"
            preview={false}
            width={15}
            height={15}
            className="rotate-180"
          />
        </button>
      )}

      <div
        ref={scrollContainerRef}
        className="flex overflow-x-auto gap-[13px] snap-x snap-mandatory no-scrollbar w-0 flex-grow"
      >
        {/* All Button */}
        <div
          className={`flex-none snap-start flex flex-col justify-center space-y-[10px] items-center w-[61px] h-[77px] 
            transition-transform cursor-pointer rounded-lg shadow-custom-light ${
            selectedCategory === "all"
              ? "bg-[#572D86]"
              : "bg-white border border-[1px] border-[#FFD700]"
          }`}
          onClick={() => setSelectedCategory("all")}
        >
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22.133"
              height="28.71"
              viewBox="0 0 22.133 28.71"
            >
              <g transform="translate(-77.824 -67.565)">
                <path
                  d="M262.419,135.013c5.84-.341,7.192,10.814,3.11,13.846a12.131,12.131,0,0,0-1.221.656c0,.722-.039,1.444-.013,2.152.079,2.953.262,5.893.289,8.859a10.4,10.4,0,0,1,.1,1.181,1.891,1.891,0,0,1-2.52,1.837c-1.3-.551-1.168-1.68-1.207-2.717-.171-3.727.144-7.573-.013-11.313-2.966-1.955-3.517-5.958-2.887-9.226.42-2.2,1.785-5.131,4.357-5.289Z"
                  transform="translate(-167.923 -67.435)"
                  fill={selectedCategory === "all" ? "#FFFFFF" : "#572D86"}
                />
                <path
                  d="M171.645,148.271l.486,13.531a1.954,1.954,0,0,1-3.832.486c.066-4.672.381-9.331.486-14.016-2.362-.171-2.966-2.441-3.005-4.462s.3-5.2.262-7.783a1.032,1.032,0,1,1,2.06.092,46.054,46.054,0,0,0-.092,5.906c.262,1.627,1.234.7,1.365-.092.276-1.785-.315-4.41.052-6.155a.878.878,0,0,1,1.732-.053c.249,1.877-.289,4.449,0,6.3s1.431.722,1.562-.092c.21-1.326-.079-3.294,0-4.725s.079-1.207.144-1.431c.276-.866,1.929-.538,1.824.446-.131,2.612.157,5.381,0,7.979-.118,1.9-.945,3.872-3.058,4.082Z"
                  transform="translate(-87.951 -67.438)"
                  fill={selectedCategory === "all" ? "#FFFFFF" : "#572D86"}
                />
              </g>
            </svg>
          </div>
          <span
            className={`text-[8px] leading-[12px] ${
              selectedCategory !== "all" ? "text-gray-700" : "text-white"
            }`}
          >
            All
          </span>
        </div>

        {/* Dynamic categories */}
        {categories.length > 0 ? (
          categories.map((category) => (
            <div
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex-none flex flex-col snap-start justify-center items-center rounded-lg w-[61px] h-[77px] 
                transition-transform cursor-pointer shadow-custom-light ${
                selectedCategory === category.id
                  ? "bg-[#572D86]"
                  : "bg-white border border-[1px] border-[#FFD700]"
              }`}
            >
              <div className="flex flex-col items-center justify-center">
                <Image
                  src={serverImgFullPath(category.image)}
                  fallback="/images/icons/no-pic-icon.svg"
                  alt={`${category.name}`}
                  preview={false}
                  width={36}
                  height={36}
                  className="w-9 h-9 mb-2 object-contain rounded-full"
                />
                <span
                  className={`text-[8px] mt-[10px] font-medium leading-[12px] ${
                    selectedCategory === category.id
                      ? "text-white"
                      : "text-[#572D86]"
                  }`}
                >
                  {category.name.length > 11
                    ? `${category.name.slice(0, 11)}...`
                    : category.name}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center gap-x-[13px]">
            {[1, 2, 3, 4, 5].map((n) => (
              <Skeleton.Node
                key={n}
                active={true}
                style={{ width: 61, height: 77 }}
              />
            ))}
          </div>
        )}
      </div>

      {showScrollButtons && (
        <button
          className="w-[29px] h-[29px] bg-[#AFD467] rounded-full flex items-center justify-center shrink-0"
          onClick={onHandleScrollRight}
        >
          <Image
            src="/images/icons/arrow.svg"
            preview={false}
            width={15}
            height={15}
          />
        </button>
      )}
    </div>
  );
}
